import { useState } from 'react';
import clsx from 'clsx';
import { caretRight } from '../../../../../../assets/hostedassets';
import RawTable from '../../../../components/Tables/RawTable/RawTable';
import sectionStyles from '../../../AuditedFinancialsVisualisation/components/AuditedStatementSection/AuditedStatementSection.module.scss';
import FinancialsIngestionNote from '../../financialsIngestion/FinancialsIngestionNote';
import { FINANCIAL_YEAR_COLUMN_LABEL } from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

interface ProvisionalOtherMisSectionProps {
    title: string;
    tableData: { headers?: any[]; rows?: any[] };
    loading: boolean;
    rawOptions: any;
    dataFormatOptions?: string[];
    metric: string;
    defaultExpanded?: boolean;
    /** RECUR-28096 — a provisional financials file is still being extracted. */
    inProgress?: boolean;
}

/**
 * The 'Other Metric (MIS)' section (RECUR-27640).
 *
 * Deliberately NOT rendered through `AuditedStatementSection`/`AuditedStatementTable` like the three
 * statutory statements. Those join a fixed layout by `classificationName`, and Other MIS cannot be
 * expressed that way:
 *
 *   - Its payload is raw ledger rows fetched for a SINGLE classification (`raw: true`,
 *     `classificationId: '1'` / Unclassified), so many rows legitimately share one
 *     `classificationName`. The audited table folds rows into a map keyed on that name
 *     (last-write-wins) and keys React rows by label — identical classifications would collapse into
 *     duplicate rows all showing the last row's figures.
 *   - Its columns are not a year matrix. The payload carries per-row Classification and Data Format
 *     metadata, which the audited table would render as if they were financial values.
 *
 * So it keeps `RawTable`, the table that has always backed this view and understands that shape.
 * What it gains is the audited section chrome — the collapsible header and the same title
 * treatment — so the page reads as one surface. Read-only (`isEditing={false}`): editing is not part
 * of this surface, matching Audited.
 */
const ProvisionalOtherMisSection = ({
    title,
    tableData,
    loading,
    rawOptions,
    dataFormatOptions,
    metric,
    defaultExpanded = true,
    inProgress = false,
}: ProvisionalOtherMisSectionProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

    const hasData = (tableData?.rows?.length ?? 0) > 0;
    const expanded = isExpanded && hasData;

    return (
        <div className={sectionStyles.Section}>
            <div className={sectionStyles.StickyBand}>
                <div className={sectionStyles.HeaderRow}>
                    <button
                        type="button"
                        className={sectionStyles.SectionHeader}
                        onClick={() => hasData && setIsExpanded((prev) => !prev)}
                        disabled={!hasData}
                        data-testid="statement-section-header"
                    >
                        {hasData && (
                            <img
                                src={caretRight}
                                alt=""
                                className={clsx(sectionStyles.Chevron, { [sectionStyles.ChevronExpanded]: expanded })}
                            />
                        )}
                        <span
                            className={clsx(sectionStyles.SectionTitle, { [sectionStyles.SectionTitleMuted]: !hasData })}
                            data-testid="statement-section-title"
                        >
                            {title}
                        </span>
                        {!hasData && !inProgress && <span className={sectionStyles.NoData}>(No data available)</span>}
                        {inProgress && <FinancialsIngestionNote />}
                    </button>
                </div>
            </div>

            {expanded && (
                <div className={sectionStyles.SectionBody}>
                    <RawTable
                        data={tableData as any}
                        loading={loading}
                        isEditing={false}
                        metric={metric}
                        rawOptions={rawOptions}
                        dataFormatOptions={dataFormatOptions}
                        changedData={[]}
                        setChangedData={() => undefined}
                        // Other Metric (MIS) renders through RawTable, not AuditedStatementSection —
                        // so unlike the three statutory sections above, it needs this override
                        // explicitly to read "Financial Year".
                        firstColumnLabel={FINANCIAL_YEAR_COLUMN_LABEL}
                    />
                </div>
            )}
        </div>
    );
};

export default ProvisionalOtherMisSection;
